import { useState, useEffect } from 'react';
import {
  Calendar,
  Clock,
  MapPin,
  User,
  Mail,
  Phone,
  Plus,
  Eye,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Search,
  FileText,
  ChevronLeft,
  ChevronRight,
  List,
  Grid3X3,
  Trash2,
  Loader2,
  Building2,
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Separator } from './ui/separator';
import { toast } from 'sonner';
import { TransferSigning } from '../types/transfer-signing';
import { signingAPI } from '../lib/signing-api';
import { logger } from '../services/logger.service';

interface TransferSigningsProps {
  selectedDealId?: string;
  onClose?: () => void;
}

const STATUS_OPTIONS = ['scheduled', 'confirmed', 'completed', 'rescheduled', 'cancelled', 'documents-pending'] as const;

const STATUS_COLORS: Record<string, string> = {
  confirmed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  scheduled: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  completed: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
  rescheduled: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  'documents-pending': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
};

function StatusIcon({ status }: { status: string }) {
  switch (status) {
    case 'confirmed':  return <CheckCircle className="w-4 h-4 text-green-500" />;
    case 'scheduled':  return <Clock className="w-4 h-4 text-blue-500" />;
    case 'completed':  return <CheckCircle className="w-4 h-4 text-gray-500" />;
    case 'rescheduled': return <AlertTriangle className="w-4 h-4 text-orange-500" />;
    case 'cancelled':  return <XCircle className="w-4 h-4 text-red-500" />;
    case 'documents-pending': return <FileText className="w-4 h-4 text-yellow-500" />;
    default: return <Clock className="w-4 h-4 text-gray-400" />;
  }
}

function formatDisplayDate(dateStr: string) {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-ZA', {
      weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch { return dateStr; }
}

function formatDisplayTime(timeStr: string) {
  if (!timeStr) return '—';
  try {
    return new Date(`2000-01-01T${timeStr}`).toLocaleTimeString('en-ZA', {
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch { return timeStr; }
}

const EMPTY_FORM = {
  title: '',
  propertyAddress: '',
  scheduledDate: '',
  scheduledTime: '',
  duration: 60,
  location: 'Conveyancer Office',
  locationAddress: '',
  buyerName: '',
  buyerEmail: '',
  buyerPhone: '',
  sellerName: '',
  sellerEmail: '',
  sellerPhone: '',
  conveyancerName: '',
  conveyancerEmail: '',
  conveyancerPhone: '',
  notes: '',
};

export function TransferSignings({ onClose }: TransferSigningsProps) {
  const [signings, setSignings] = useState<TransferSigning[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [currentDate, setCurrentDate] = useState(new Date());

  // Dialogs
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedSigning, setSelectedSigning] = useState<TransferSigning | null>(null);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [showRescheduleDialog, setShowRescheduleDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [actionSigningId, setActionSigningId] = useState<string | null>(null);

  // Form state
  const [form, setForm] = useState(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Completion / reschedule
  const [completionNotes, setCompletionNotes] = useState('');
  const [rescheduleData, setRescheduleData] = useState({ date: '', time: '', reason: '' });

  // ── Load ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    loadSignings();
  }, []);

  const loadSignings = async () => {
    try {
      setIsLoading(true);
      const data = await signingAPI.getSignings();
      setSignings(data);
    } catch (err) {
      logger.error('Failed to load signings', err as Error, undefined, 'TransferSignings');
      toast.error('Failed to load transfer signings');
      setSignings([]);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Computed ──────────────────────────────────────────────────────────────

  const stats = {
    total: signings.length,
    scheduled: signings.filter(s => s.status === 'scheduled').length,
    confirmed: signings.filter(s => s.status === 'confirmed').length,
    completed: signings.filter(s => s.status === 'completed').length,
    thisWeek: signings.filter(s => {
      if (!s.scheduledDate) return false;
      const d = new Date(s.scheduledDate);
      const now = new Date();
      const start = new Date(now); start.setDate(now.getDate() - now.getDay());
      const end = new Date(start); end.setDate(start.getDate() + 7);
      return d >= start && d < end;
    }).length,
  };

  const filteredSignings = signings.filter(s => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = !term ||
      (s.dealTitle || '').toLowerCase().includes(term) ||
      (s.propertyAddress || '').toLowerCase().includes(term) ||
      (s.conveyancerName || '').toLowerCase().includes(term) ||
      (s.signers || []).some(p => (p.name || '').toLowerCase().includes(term));
    const matchesStatus = statusFilter === 'all' || s.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const upcomingSignings = filteredSignings
    .filter(s => s.status !== 'completed' && s.status !== 'cancelled')
    .sort((a, b) => {
      const da = new Date(`${a.scheduledDate}T${a.scheduledTime || '00:00'}`).getTime();
      const db = new Date(`${b.scheduledDate}T${b.scheduledTime || '00:00'}`).getTime();
      return da - db;
    });

  // ── Create ────────────────────────────────────────────────────────────────

  const handleCreate = async () => {
    if (!form.title || !form.scheduledDate || !form.scheduledTime) {
      toast.error('Title, date and time are required');
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        title: form.title,
        dealTitle: form.title,
        propertyAddress: form.propertyAddress,
        status: 'scheduled' as const,
        scheduledDate: form.scheduledDate,
        scheduledTime: form.scheduledTime,
        duration: form.duration,
        location: form.location,
        locationAddress: form.locationAddress,
        buyerName: form.buyerName,
        buyerEmail: form.buyerEmail,
        buyerPhone: form.buyerPhone,
        sellerName: form.sellerName,
        sellerEmail: form.sellerEmail,
        sellerPhone: form.sellerPhone,
        conveyancerName: form.conveyancerName,
        conveyancerEmail: form.conveyancerEmail,
        conveyancerPhone: form.conveyancerPhone,
        notes: form.notes,
        signers: [
          ...(form.buyerName ? [{ id: `buyer-${Date.now()}`, type: 'buyer', name: form.buyerName, email: form.buyerEmail, phone: form.buyerPhone, confirmed: false }] : []),
          ...(form.sellerName ? [{ id: `seller-${Date.now()}`, type: 'seller', name: form.sellerName, email: form.sellerEmail, phone: form.sellerPhone, confirmed: false }] : []),
        ],
        documentChecklist: [
          { id: `d1-${Date.now()}`, name: 'Original ID Document', required: true, completed: false },
          { id: `d2-${Date.now()}`, name: 'Proof of Address', required: true, completed: false },
          { id: `d3-${Date.now()}`, name: 'Signed Offer to Purchase', required: true, completed: false },
          { id: `d4-${Date.now()}`, name: 'Bond Approval Letter', required: true, completed: false },
          { id: `d5-${Date.now()}`, name: 'FICA Documentation', required: true, completed: false },
        ],
        reminderSent: false,
        confirmationSent: false,
        history: [],
        createdBy: 'admin',
      };
      const created = await signingAPI.createSigning(payload);
      setSignings(prev => [created, ...prev]);
      setIsCreateOpen(false);
      setForm(EMPTY_FORM);
      toast.success('Transfer signing scheduled successfully');
    } catch (err) {
      logger.error('Failed to create signing', err as Error, undefined, 'TransferSignings');
      toast.error('Failed to schedule signing');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Complete ──────────────────────────────────────────────────────────────

  const handleComplete = async () => {
    if (!actionSigningId) return;
    try {
      await signingAPI.updateSigning(actionSigningId, {
        status: 'completed',
        completedAt: new Date().toISOString(),
        notes: completionNotes || undefined,
      } as any);
      setSignings(prev => prev.map(s =>
        s.id === actionSigningId
          ? { ...s, status: 'completed' as const, completedAt: new Date().toISOString() }
          : s
      ));
      toast.success('Transfer signing marked as completed');
    } catch (err) {
      toast.error('Failed to complete signing');
    } finally {
      setShowCompleteDialog(false);
      setCompletionNotes('');
      setActionSigningId(null);
    }
  };

  // ── Reschedule ────────────────────────────────────────────────────────────

  const handleReschedule = async () => {
    if (!actionSigningId || !rescheduleData.date || !rescheduleData.time) {
      toast.error('New date and time are required');
      return;
    }
    try {
      await signingAPI.updateSigning(actionSigningId, {
        status: 'rescheduled',
        scheduledDate: rescheduleData.date,
        scheduledTime: rescheduleData.time,
        notes: rescheduleData.reason || undefined,
      } as any);
      setSignings(prev => prev.map(s =>
        s.id === actionSigningId
          ? { ...s, status: 'rescheduled' as const, scheduledDate: rescheduleData.date, scheduledTime: rescheduleData.time }
          : s
      ));
      toast.success('Signing rescheduled');
    } catch (err) {
      toast.error('Failed to reschedule signing');
    } finally {
      setShowRescheduleDialog(false);
      setRescheduleData({ date: '', time: '', reason: '' });
      setActionSigningId(null);
    }
  };

  // ── Confirm ───────────────────────────────────────────────────────────────

  const handleConfirm = async (id: string) => {
    try {
      await signingAPI.updateSigning(id, { status: 'confirmed' } as any);
      setSignings(prev => prev.map(s => s.id === id ? { ...s, status: 'confirmed' as const } : s));
      toast.success('Signing confirmed');
    } catch (err) {
      toast.error('Failed to confirm signing');
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────

  const handleDelete = async () => {
    if (!actionSigningId) return;
    try {
      await signingAPI.deleteSigning(actionSigningId);
      setSignings(prev => prev.filter(s => s.id !== actionSigningId));
      toast.success('Signing deleted');
    } catch (err) {
      toast.error('Failed to delete signing');
    } finally {
      setShowDeleteDialog(false);
      setActionSigningId(null);
    }
  };

  // ── Calendar helpers ───────────────────────────────────────────────────────

  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();

  const getSigningsForDate = (dateStr: string) =>
    signings.filter(s => s.scheduledDate === dateStr);

  const toCalDate = (d: Date) => d.toISOString().split('T')[0];

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center space-x-6">
          <div>
            <h3>Transfer Signing Appointments</h3>
            <p className="text-muted-foreground text-sm">
              Schedule and manage transfer document signings
            </p>
          </div>
          <div className="flex items-center space-x-1 border rounded-lg p-1">
            <Button variant={viewMode === 'list' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('list')} className="px-3" data-testid="transfer-signing-view-list-button">
              <List className="w-4 h-4 mr-2" />List
            </Button>
            <Button variant={viewMode === 'calendar' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('calendar')} className="px-3" data-testid="transfer-signing-view-calendar-button">
              <Grid3X3 className="w-4 h-4 mr-2" />Calendar
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {onClose && <Button variant="outline" onClick={onClose} data-testid="transfer-signing-close-button">Close</Button>}
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button data-testid="transfer-signing-schedule-button">
                <Plus className="w-4 h-4 mr-2" />Schedule Signing
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Schedule Transfer Signing</DialogTitle>
                <DialogDescription>Create a new transfer signing appointment</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 pt-2">

                {/* Title + Address */}
                <div className="space-y-2">
                  <Label>Signing Title *</Label>
                  <Input placeholder="e.g. Smith Property Transfer" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label>Property Address</Label>
                  <Input placeholder="123 Main Street, Cape Town" value={form.propertyAddress} onChange={e => setForm(f => ({ ...f, propertyAddress: e.target.value }))} />
                </div>

                {/* Date / Time / Duration */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Date *</Label>
                    <Input type="date" value={form.scheduledDate} min={new Date().toISOString().split('T')[0]} onChange={e => setForm(f => ({ ...f, scheduledDate: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Time *</Label>
                    <Input type="time" value={form.scheduledTime} onChange={e => setForm(f => ({ ...f, scheduledTime: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Duration (min)</Label>
                    <Select value={form.duration.toString()} onValueChange={v => setForm(f => ({ ...f, duration: parseInt(v) }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[30, 60, 90, 120, 180].map(d => <SelectItem key={d} value={d.toString()}>{d} min</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Location */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Location Type</Label>
                    <Select value={form.location} onValueChange={v => setForm(f => ({ ...f, location: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['Conveyancer Office', 'Property', 'Our Office', 'Client Location', 'Virtual'].map(l =>
                          <SelectItem key={l} value={l}>{l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Location Address</Label>
                    <Input placeholder="Full address or meeting link" value={form.locationAddress} onChange={e => setForm(f => ({ ...f, locationAddress: e.target.value }))} />
                  </div>
                </div>

                <Separator />

                {/* Buyer */}
                <p className="font-medium text-sm">Buyer Details</p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2"><Label>Name</Label><Input placeholder="Full name" value={form.buyerName} onChange={e => setForm(f => ({ ...f, buyerName: e.target.value }))} /></div>
                  <div className="space-y-2"><Label>Email</Label><Input type="email" placeholder="buyer@email.com" value={form.buyerEmail} onChange={e => setForm(f => ({ ...f, buyerEmail: e.target.value }))} /></div>
                  <div className="space-y-2"><Label>Phone</Label><Input placeholder="+27 82 000 0000" value={form.buyerPhone} onChange={e => setForm(f => ({ ...f, buyerPhone: e.target.value }))} /></div>
                </div>

                {/* Seller */}
                <p className="font-medium text-sm">Seller Details</p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2"><Label>Name</Label><Input placeholder="Full name" value={form.sellerName} onChange={e => setForm(f => ({ ...f, sellerName: e.target.value }))} /></div>
                  <div className="space-y-2"><Label>Email</Label><Input type="email" placeholder="seller@email.com" value={form.sellerEmail} onChange={e => setForm(f => ({ ...f, sellerEmail: e.target.value }))} /></div>
                  <div className="space-y-2"><Label>Phone</Label><Input placeholder="+27 82 000 0000" value={form.sellerPhone} onChange={e => setForm(f => ({ ...f, sellerPhone: e.target.value }))} /></div>
                </div>

                <Separator />

                {/* Conveyancer */}
                <p className="font-medium text-sm">Conveyancer Details</p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2"><Label>Name</Label><Input placeholder="Conveyancer name" value={form.conveyancerName} onChange={e => setForm(f => ({ ...f, conveyancerName: e.target.value }))} /></div>
                  <div className="space-y-2"><Label>Email</Label><Input type="email" placeholder="conv@law.co.za" value={form.conveyancerEmail} onChange={e => setForm(f => ({ ...f, conveyancerEmail: e.target.value }))} /></div>
                  <div className="space-y-2"><Label>Phone</Label><Input placeholder="+27 11 000 0000" value={form.conveyancerPhone} onChange={e => setForm(f => ({ ...f, conveyancerPhone: e.target.value }))} /></div>
                </div>

                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea placeholder="Additional notes or special instructions..." value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={3} />
                </div>

                <div className="flex gap-2 pt-2">
                  <Button variant="outline" className="flex-1" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
                  <Button className="flex-1" onClick={handleCreate} disabled={isSubmitting}>
                    {isSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                    Schedule Signing
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: 'Total', value: stats.total, icon: <FileText className="h-4 w-4 text-muted-foreground" /> },
          { label: 'Scheduled', value: stats.scheduled, icon: <Clock className="h-4 w-4 text-blue-500" /> },
          { label: 'Confirmed', value: stats.confirmed, icon: <CheckCircle className="h-4 w-4 text-green-500" /> },
          { label: 'Completed', value: stats.completed, icon: <CheckCircle className="h-4 w-4 text-gray-500" /> },
          { label: 'This Week', value: stats.thisWeek, icon: <Calendar className="h-4 w-4 text-muted-foreground" /> },
        ].map(stat => (
          <Card key={stat.label}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{stat.label}</CardTitle>
              {stat.icon}
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stat.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* ── Search + Filter ── */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search signings, properties, parties..."
            className="pl-9"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {STATUS_OPTIONS.map(s => (
              <SelectItem key={s} value={s}>{s.replace('-', ' ').replace(/\b\w/g, c => c.toUpperCase())}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* ── LIST VIEW ── */}
      {viewMode === 'list' && (
        <div className="space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground mr-2" />
              <span className="text-muted-foreground">Loading signings...</span>
            </div>
          ) : filteredSignings.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <FileText className="w-12 h-12 text-muted-foreground/40 mx-auto mb-4" />
                <p className="text-muted-foreground">
                  {signings.length === 0
                    ? 'No signings yet. Click "Schedule Signing" to create one.'
                    : 'No signings match your search.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredSignings.map(signing => (
              <Card key={signing.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">

                    {/* Left: Info */}
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-3 flex-wrap">
                        <StatusIcon status={signing.status} />
                        <h4 className="font-semibold">{signing.dealTitle || 'Untitled Signing'}</h4>
                        <Badge className={STATUS_COLORS[signing.status] || STATUS_COLORS.scheduled}>
                          {(signing.status || 'scheduled').replace('-', ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        </Badge>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-sm text-muted-foreground">
                        {signing.propertyAddress && (
                          <div className="flex items-center gap-1.5">
                            <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                            <span className="truncate">{signing.propertyAddress}</span>
                          </div>
                        )}
                        {signing.scheduledDate && (
                          <div className="flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                            <span>{formatDisplayDate(signing.scheduledDate)} {signing.scheduledTime && `at ${formatDisplayTime(signing.scheduledTime)}`}</span>
                          </div>
                        )}
                        {signing.location && (
                          <div className="flex items-center gap-1.5">
                            <Building2 className="w-3.5 h-3.5 flex-shrink-0" />
                            <span>{signing.location}{signing.locationAddress ? ` — ${signing.locationAddress}` : ''}</span>
                          </div>
                        )}
                        {signing.conveyancerName && (
                          <div className="flex items-center gap-1.5">
                            <User className="w-3.5 h-3.5 flex-shrink-0" />
                            <span>{signing.conveyancerName}</span>
                          </div>
                        )}
                      </div>

                      {/* Parties */}
                      {(signing.signers || []).length > 0 && (
                        <div className="flex items-center gap-3 text-xs text-muted-foreground pt-1">
                          {signing.signers.map(p => (
                            <span key={p.id} className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {p.name || '—'} <span className="opacity-60">({p.type})</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Right: Actions */}
                    <div className="flex flex-col gap-2 flex-shrink-0">
                      <Button
                        size="sm" variant="outline"
                        onClick={() => { setSelectedSigning(signing); setIsDetailOpen(true); }}
                        data-testid="transfer-signing-view-details-button"
                      >
                        <Eye className="w-4 h-4 mr-1" />View
                      </Button>

                      {signing.status === 'scheduled' && (
                        <Button
                          size="sm" variant="outline"
                          className="border-green-500 text-green-600 hover:bg-green-50"
                          onClick={() => handleConfirm(signing.id)}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />Confirm
                        </Button>
                      )}

                      {(signing.status === 'scheduled' || signing.status === 'confirmed') && (
                        <>
                          <Button
                            size="sm" variant="outline"
                            className="border-blue-500 text-blue-600 hover:bg-blue-50"
                            onClick={() => { setActionSigningId(signing.id); setShowCompleteDialog(true); }}
                            data-testid="transfer-signing-complete-button"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />Complete
                          </Button>
                          <Button
                            size="sm" variant="outline"
                            className="border-orange-500 text-orange-600 hover:bg-orange-50"
                            onClick={() => { setActionSigningId(signing.id); setShowRescheduleDialog(true); }}
                          >
                            <Clock className="w-4 h-4 mr-1" />Reschedule
                          </Button>
                        </>
                      )}

                      <Button
                        size="sm" variant="ghost"
                        className="text-red-500 hover:bg-red-50"
                        onClick={() => { setActionSigningId(signing.id); setShowDeleteDialog(true); }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* ── CALENDAR VIEW ── */}
      {viewMode === 'calendar' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>
                {currentDate.toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' })}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={() => setCurrentDate(d => { const n = new Date(d); n.setMonth(n.getMonth() - 1); return n; })}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date())}>Today</Button>
                <Button variant="outline" size="icon" onClick={() => setCurrentDate(d => { const n = new Date(d); n.setMonth(n.getMonth() + 1); return n; })}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Day headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                <div key={d} className="text-center text-xs font-medium text-muted-foreground py-1">{d}</div>
              ))}
            </div>
            {/* Day cells */}
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: firstDayOfMonth }).map((_, i) => (
                <div key={`empty-${i}`} className="h-20" />
              ))}
              {Array.from({ length: daysInMonth }, (_, i) => {
                const day = i + 1;
                const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const daySignings = getSigningsForDate(dateStr);
                const isToday = dateStr === toCalDate(new Date());
                return (
                  <div key={day} className={`h-20 border rounded-lg p-1 text-xs overflow-hidden ${isToday ? 'border-primary bg-primary/5' : 'border-border'}`}>
                    <div className={`font-medium mb-0.5 ${isToday ? 'text-primary' : ''}`}>{day}</div>
                    {daySignings.slice(0, 2).map(s => (
                      <div
                        key={s.id}
                        className={`truncate rounded px-1 py-0.5 cursor-pointer mb-0.5 ${STATUS_COLORS[s.status] || STATUS_COLORS.scheduled}`}
                        onClick={() => { setSelectedSigning(s); setIsDetailOpen(true); }}
                        title={s.dealTitle}
                      >
                        {s.scheduledTime ? `${s.scheduledTime} ` : ''}{(s.dealTitle || '').split(' ')[0]}
                      </div>
                    ))}
                    {daySignings.length > 2 && (
                      <div className="text-muted-foreground text-xs">+{daySignings.length - 2} more</div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── DETAIL DIALOG ── */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedSigning && (
            <>
              <DialogHeader>
                <DialogTitle>{selectedSigning.dealTitle || 'Signing Details'}</DialogTitle>
                <DialogDescription>
                  <Badge className={STATUS_COLORS[selectedSigning.status] || STATUS_COLORS.scheduled}>
                    {(selectedSigning.status || '').replace('-', ' ').replace(/\b\w/g, c => c.toUpperCase())}
                  </Badge>
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 pt-2">

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground text-xs mb-1">Property</p>
                    <p className="font-medium">{selectedSigning.propertyAddress || '—'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs mb-1">Date & Time</p>
                    <p className="font-medium">
                      {formatDisplayDate(selectedSigning.scheduledDate)}
                      {selectedSigning.scheduledTime && ` at ${formatDisplayTime(selectedSigning.scheduledTime)}`}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs mb-1">Location</p>
                    <p className="font-medium">{selectedSigning.location || '—'}</p>
                    {selectedSigning.locationAddress && <p className="text-muted-foreground text-xs">{selectedSigning.locationAddress}</p>}
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs mb-1">Duration</p>
                    <p className="font-medium">{selectedSigning.duration || 60} minutes</p>
                  </div>
                </div>

                <Separator />

                {/* Parties */}
                {(selectedSigning.signers || []).length > 0 && (
                  <div>
                    <p className="font-medium text-sm mb-2">Parties</p>
                    <div className="space-y-2">
                      {selectedSigning.signers.map(p => (
                        <div key={p.id} className="flex items-center gap-3 text-sm p-2 rounded-lg bg-muted/40">
                          <User className="w-4 h-4 text-muted-foreground" />
                          <div>
                            <span className="font-medium">{p.name}</span>
                            <Badge variant="outline" className="ml-2 text-xs capitalize">{p.type}</Badge>
                          </div>
                          {p.email && <div className="flex items-center gap-1 text-muted-foreground ml-auto"><Mail className="w-3 h-3" />{p.email}</div>}
                          {p.phone && <div className="flex items-center gap-1 text-muted-foreground"><Phone className="w-3 h-3" />{p.phone}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Conveyancer */}
                {selectedSigning.conveyancerName && (
                  <div>
                    <p className="font-medium text-sm mb-2">Conveyancer</p>
                    <div className="flex items-center gap-3 text-sm p-2 rounded-lg bg-muted/40">
                      <Building2 className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">{selectedSigning.conveyancerName}</span>
                      {selectedSigning.conveyancerEmail && <div className="flex items-center gap-1 text-muted-foreground ml-auto"><Mail className="w-3 h-3" />{selectedSigning.conveyancerEmail}</div>}
                      {selectedSigning.conveyancerContact && <div className="flex items-center gap-1 text-muted-foreground"><Phone className="w-3 h-3" />{selectedSigning.conveyancerContact}</div>}
                    </div>
                  </div>
                )}

                {/* Documents */}
                {(selectedSigning.documentChecklist || []).length > 0 && (
                  <div>
                    <p className="font-medium text-sm mb-2">Document Checklist</p>
                    <div className="space-y-1">
                      {selectedSigning.documentChecklist.map(doc => (
                        <div key={doc.id} className={`flex items-center gap-2 text-sm p-1.5 rounded ${doc.completed ? 'opacity-60' : ''}`}>
                          <CheckCircle className={`w-4 h-4 flex-shrink-0 ${doc.completed ? 'text-green-500' : 'text-muted-foreground'}`} />
                          <span className={doc.completed ? 'line-through' : ''}>{doc.name}</span>
                          {doc.required && <Badge variant="outline" className="text-xs ml-auto">Required</Badge>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Notes */}
                {selectedSigning.notes && (
                  <div>
                    <p className="font-medium text-sm mb-1">Notes</p>
                    <p className="text-sm text-muted-foreground">{selectedSigning.notes}</p>
                  </div>
                )}

                {/* Actions */}
                {(selectedSigning.status === 'scheduled' || selectedSigning.status === 'confirmed') && (
                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="outline"
                      onClick={() => { setActionSigningId(selectedSigning.id); setIsDetailOpen(false); setShowRescheduleDialog(true); }}
                    >
                      <Clock className="w-4 h-4 mr-1" />Reschedule
                    </Button>
                    <Button
                      onClick={() => { setActionSigningId(selectedSigning.id); setIsDetailOpen(false); setShowCompleteDialog(true); }}
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />Mark Complete
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ── COMPLETE DIALOG ── */}
      <Dialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark as Completed</DialogTitle>
            <DialogDescription>Confirm this transfer signing has been completed</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="space-y-2">
              <Label>Completion Notes (optional)</Label>
              <Textarea
                placeholder="Any notes about the signing completion..."
                value={completionNotes}
                onChange={e => setCompletionNotes(e.target.value)}
                rows={3}
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowCompleteDialog(false)}>Cancel</Button>
              <Button className="flex-1" onClick={handleComplete}>
                <CheckCircle className="w-4 h-4 mr-1" />Confirm Completion
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── RESCHEDULE DIALOG ── */}
      <Dialog open={showRescheduleDialog} onOpenChange={setShowRescheduleDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reschedule Signing</DialogTitle>
            <DialogDescription>Set a new date and time for this signing</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>New Date *</Label>
                <Input type="date" min={new Date().toISOString().split('T')[0]} value={rescheduleData.date} onChange={e => setRescheduleData(d => ({ ...d, date: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>New Time *</Label>
                <Input type="time" value={rescheduleData.time} onChange={e => setRescheduleData(d => ({ ...d, time: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Reason (optional)</Label>
              <Input placeholder="Reason for rescheduling" value={rescheduleData.reason} onChange={e => setRescheduleData(d => ({ ...d, reason: e.target.value }))} />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowRescheduleDialog(false)}>Cancel</Button>
              <Button className="flex-1" onClick={handleReschedule}>Confirm Reschedule</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── DELETE DIALOG ── */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Signing</DialogTitle>
            <DialogDescription>This action cannot be undone. The signing will be permanently removed.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button variant="destructive" className="flex-1" onClick={handleDelete}>
              <Trash2 className="w-4 h-4 mr-1" />Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
